// Accessibility settings (#23). Saved on this device only (localStorage), never
// sent anywhere. Applied as classes on <html>, so plain CSS in index.css does the
// work. index.html applies them before the app loads, so there is no flash.

import { useSyncExternalStore } from 'react';

export type TextSize = 0 | 1 | 2 | 3; // 100%, 112.5%, 125%, 137.5%
export type A11ySettings = {
  textSize: TextSize;
  spacing: boolean;     // more line, letter and word spacing
  contrast: boolean;    // stronger text and borders, underlined links
  readableFont: boolean; // Atkinson Hyperlegible, made for low vision
  readAloud: boolean;   // read incoming chat messages aloud (browser voice)
};

export const A11Y_DEFAULTS: A11ySettings = { textSize: 0, spacing: false, contrast: false, readableFont: false, readAloud: false };
const KEY = 'zuzu_a11y';

const load = (): A11ySettings => {
  try { return { ...A11Y_DEFAULTS, ...JSON.parse(localStorage.getItem(KEY) || '{}') }; } catch { return A11Y_DEFAULTS; }
};

let current = load();
const listeners = new Set<() => void>();

// The readable font is loaded only when someone turns it on.
const loadFont = () => {
  if (document.getElementById('a11y-font')) return;
  const link = document.createElement('link');
  link.id = 'a11y-font';
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Atkinson+Hyperlegible:ital,wght@0,400;0,700;1,400&display=swap';
  document.head.appendChild(link);
};

export const applyA11y = (s: A11ySettings) => {
  const c = document.documentElement.classList;
  for (let i = 1; i <= 3; i++) c.toggle(`a11y-text-${i}`, s.textSize === i);
  c.toggle('a11y-spacing', s.spacing);
  c.toggle('a11y-contrast', s.contrast);
  c.toggle('a11y-font', s.readableFont);
  if (s.readableFont) loadFont();
};

export const setA11y = (patch: Partial<A11ySettings>) => {
  current = { ...current, ...patch };
  try { localStorage.setItem(KEY, JSON.stringify(current)); } catch { /* private mode: works for this visit */ }
  applyA11y(current);
  if (patch.readAloud === false) window.speechSynthesis?.cancel();
  listeners.forEach(l => l());
};

export const resetA11y = () => setA11y(A11Y_DEFAULTS);
export const getA11y = () => current;

export const useA11y = () => useSyncExternalStore(
  (l) => { listeners.add(l); return () => listeners.delete(l); },
  () => current,
  () => A11Y_DEFAULTS,
);

// --- Read aloud: incoming messages only, with the browser's own voice. ---
// Nothing is sent anywhere; speech runs in the browser. The voice language is
// picked from the text's script, so Hindi or Arabic are not read with an English voice.
const SCRIPT_LANGS: [RegExp, string][] = [
  [/[\u0A80-\u0AFF]/, 'gu-IN'], [/[\u0900-\u097F]/, 'hi-IN'], [/[\u0600-\u06FF]/, 'ar'], [/[\u0980-\u09FF]/, 'bn-IN'],
  [/[\u0B80-\u0BFF]/, 'ta-IN'], [/[\u3040-\u30FF]/, 'ja-JP'], [/[\uAC00-\uD7AF]/, 'ko-KR'], [/[\u4E00-\u9FFF]/, 'zh-CN'],
  [/[\u0400-\u04FF]/, 'ru-RU'], [/[\u0E00-\u0E7F]/, 'th-TH'],
];

export const canReadAloud = () => typeof window !== 'undefined' && 'speechSynthesis' in window;

// Only voices that run on the device: some browsers also offer online voices,
// which would send the message text to another company. With no on-device
// voice for a language, that message is skipped.
const localVoice = (lang: string) => {
  const base = lang.toLowerCase().split('-')[0];
  const voices = window.speechSynthesis.getVoices().filter(v => v.localService);
  return voices.find(v => v.lang.toLowerCase().replace('_', '-') === lang.toLowerCase())
    ?? voices.find(v => v.lang.toLowerCase().startsWith(base));
};
if (canReadAloud()) window.speechSynthesis.getVoices(); // starts loading the voice list

export const readAloud = (text: string) => {
  if (!current.readAloud || !canReadAloud() || !text.trim()) return;
  const lang = SCRIPT_LANGS.find(([re]) => re.test(text))?.[1] ?? navigator.language;
  const voice = localVoice(lang);
  if (!voice) return;
  const u = new SpeechSynthesisUtterance(text);
  u.voice = voice;
  u.lang = voice.lang;
  window.speechSynthesis.speak(u);
};

// --- One menu for the whole app: any header button (or Alt + A) opens it, and
// focus goes back to the button that opened it. ---
let menuOpen = false;
let opener: HTMLElement | null = null;
const menuListeners = new Set<() => void>();
export const setA11yMenu = (open: boolean, from?: HTMLElement | null) => {
  if (open && from) opener = from;
  menuOpen = open;
  menuListeners.forEach(l => l());
  if (!open) { opener?.focus(); opener = null; }
};
export const useA11yMenu = () => useSyncExternalStore(
  (l) => { menuListeners.add(l); return () => menuListeners.delete(l); },
  () => menuOpen,
  () => false,
);
